import React, { useEffect, useState } from "react";
import {
    Box,
    Card,
    CircularProgress,
    Stack,
    Typography,
    Button,
    Alert,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { ROUTES } from "../../../shared/utils";
import { integrationApi, SaveWhoopUserRequest } from "../api/integration";
import { AnalyticsEvent, trackEvent } from "../../../lib/analytics";
import dayjs from "dayjs";

export const WhoopCallback: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [error, setError] = useState<string | null>(null);
    const [processing, setProcessing] = useState(true);

    useEffect(() => {
        const cookieDomain = window.location.hostname.endsWith("peak4.technology")
            ? "; Domain=.peak4.technology"
            : "";
        const secureAttr = window.location.protocol === "https:" ? "; Secure" : "";

        const params = new URLSearchParams(location.search);
        const code = params.get("code");
        const state = params.get("state");
        const oauthError = params.get("error");

        if (oauthError) {
            setError(`Whoop authorisation error: ${oauthError}`);
            setProcessing(false);
            return;
        }

        const savedState = sessionStorage.getItem("whoop_oauth_state");

        if (!state || state.length < 8) {
            setError("Invalid OAuth state returned — please reconnect.");
            setProcessing(false);
            return;
        }
        if (!savedState || savedState !== state) {
            setError("OAuth state mismatch — please try again.");
            setProcessing(false);
            return;
        }

        if (!code) {
            setError("Missing authorisation code — please reconnect.");
            setProcessing(false);
            return;
        }

        const exchangeAndSave = async () => {
            try {
                // Exchange the code for tokens via backend
                const tokenData = await integrationApi.exchangeWhoopToken(
                    code,
                    window.location.origin + "/whoop-callback",
                    state || undefined
                );
                const { accessToken, refreshToken, expiresIn, profile, device } = tokenData.data;

                // Prepare save payload
                const saveData: SaveWhoopUserRequest = {
                    accessToken,
                    refreshToken,
                    expiresAt: dayjs().add(expiresIn, 'seconds').format(),
                    profile: {
                        id: profile.user_id,
                        firstName: profile.first_name,
                        lastName: profile.last_name,
                        email: profile.email
                    }
                };

                await integrationApi.saveWhoopUser(saveData);

                trackEvent(AnalyticsEvent.DeviceConnected, { provider: "whoop" });

                navigate(ROUTES.COMPLETE, {
                    state: { deviceConnected: !!device, provider: "whoop" },
                    replace: true,
                });
            } catch (err: any) {
                console.error("Whoop OAuth error:", err);
                setError(
                    err?.response?.data?.error_description ||
                    err?.message ||
                    "Unable to connect to Whoop at the moment."
                );
            } finally {
                // Clear expected state cookie once callback handling is complete.
                document.cookie = `whoop_oauth_state=; Path=/; Max-Age=0; SameSite=Lax${secureAttr}${cookieDomain}`;
                setProcessing(false);
            }
        };

        exchangeAndSave();
    }, [location.search, navigate]);

    const handleRetry = () => navigate(ROUTES.CONNECT_DEVICE);
    const handleGoDashboard = () => navigate(ROUTES.DASHBOARD);

    return (
        <Box
            sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: { xs: 3, md: 6 },
            }}
        >
            <Card
                variant="outlined"
                sx={{
                    width: "100%",
                    maxWidth: 520,
                    borderRadius: 4,
                    p: { xs: 4, md: 5 },
                    textAlign: "center",
                }}
            >
                <Stack spacing={3} alignItems="center">
                    {processing && (
                        <>
                            <CircularProgress />
                            <Typography variant="body1">
                                Finalising your Whoop connection…
                            </Typography>
                        </>
                    )}

                    {!processing && error && (
                        <>
                            <Alert severity="error" sx={{ width: "100%" }}>
                                {error}
                            </Alert>
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                                <Button variant="contained" onClick={handleRetry}>
                                    Try again
                                </Button>
                                <Button variant="outlined" onClick={handleGoDashboard}>
                                    Go to dashboard
                                </Button>
                            </Stack>
                        </>
                    )}
                </Stack>
            </Card>
        </Box>
    );
};
